/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  accessAPI,
  Boton,
  Titulo,
  CampoInformacion,
  Formulario,
  ArchivoPreview,
  SubidorArchivos,
  formatFecha, tituloBase } from "../../../../shared";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Sidebar, TablaFiltrable } from "../../../../shared";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { InputSwitch } from "primereact/inputswitch";
import { Tag } from "primereact/tag";
import { Menu } from "primereact/menu";
import { marked } from "marked";
import DOMPurify from "dompurify";
import renderMathInElement from "katex/contrib/auto-render";
import "katex/dist/katex.min.css";
import CardEquivalencias from "./CardEquivalencias";

export default function Norma() {
  const [norma, setNorma] = useState(null);
  const [sidebarEditar, setSidebarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [puntoActualizandoIdx, setPuntoActualizandoIdx] = useState(null);
  const [sidebarNuevoPunto, setSidebarNuevoPunto] = useState(false);
  const [loaderNuevoPunto, setLoaderNuevoPunto] = useState(false);
  const [generandoAnalisis, setGenerandoAnalisis] = useState(false);
  const [sidebarAnalisis, setSidebarAnalisis] = useState(false);
  const [editandoAnalisis, setEditandoAnalisis] = useState(false);
  const [loaderEditarAnalisis, setLoaderEditarAnalisis] = useState(false);
  const analisisRef = useRef(null);
  const { normaId } = useParams();
  const toast = useRef(null);
  const menuRef = useRef(null);
  const filaSeleccionada = useRef(null);

  useEffect(() => {
    cargarNorma();
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling mientras se analizan los capítulos (igual que la sección de documentos).
  // Cada 5s consulta el estado; cuando deja de estar "procesando", actualiza los
  // puntos extraídos y frena.
  useEffect(() => {
    if (norma?.estadocapitulos?.status !== "procesando") return;
    const intervalId = setInterval(() => {
      accessAPI(
        "GET",
        `admin/norma/${normaId}/estadocapitulos`,
        null,
        (respuesta) => {
          if (respuesta.estadocapitulos?.status === "procesando") return;
          setNorma((prev) => ({
            ...prev,
            estadocapitulos: respuesta.estadocapitulos,
            puntos: respuesta.puntos ?? prev.puntos
          }));
        },
        () => {}
      );
    }, 5000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [norma?.estadocapitulos?.status, normaId]);

  // Renderiza las fórmulas LaTeX ($...$ / $$...$$) del análisis con KaTeX.
  // Se llama desde el onShow del Sidebar (cuando el contenido ya está montado).
  function renderizarFormulas() {
    if (!analisisRef.current) return;
    try {
      renderMathInElement(analisisRef.current, {
        delimiters: [
          { left: "$$", right: "$$", display: true },
          { left: "$", right: "$", display: false }
        ],
        throwOnError: false,
        // En LaTeX un "%" sin escapar comenta el resto de la fórmula: lo escapamos.
        preProcess: (math) => math.replace(/(?<!\\)%/g, "\\%")
      });
    } catch (e) {
      /* ignora errores de parseo de fórmulas */
    }
  }

  // Al volver de edición a vista (con el sidebar abierto), re-renderiza fórmulas.
  useEffect(() => {
    if (sidebarAnalisis && !editandoAnalisis) {
      const id = setTimeout(renderizarFormulas, 0);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editandoAnalisis, norma?.resumenAsistente]);

  const editarAnalisisFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "resumenAsistente",
            tipoitemformularioid: 11,
            etiqueta: "Análisis de requerimientos",
            respuesta: norma?.resumenAsistente
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Cancelar",
        callback: () => setEditandoAnalisis(false),
        validar: false,
        transparente: true
      },
      {
        etiqueta: "Guardar",
        callback: guardarAnalisisEditado,
        validar: false,
        clases: "verde"
      }
    ]
  };

  function guardarAnalisisEditado(campos) {
    setLoaderEditarAnalisis(true);
    accessAPI(
      "PUT",
      `admin/norma/${normaId}/analisis-requerimientos`,
      { resumenAsistente: campos.resumenAsistente },
      (r) => {
        setLoaderEditarAnalisis(false);
        setEditandoAnalisis(false);
        setNorma((prev) => ({
          ...prev,
          resumenAsistente: r.resumenAsistente,
          resumenAsistenteFecha: r.resumenAsistenteFecha
        }));
        toast.current?.show({
          severity: "success",
          summary: "Guardado",
          detail: "Análisis actualizado",
          life: 3000
        });
      },
      (r) => {
        setLoaderEditarAnalisis(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: r?.[0]?.msg || "No se pudo guardar el análisis",
          sticky: true
        });
      }
    );
  }

  // Dispara la generación del análisis de requerimientos y hace polling.
  function generarAnalisis() {
    const fechaPrevia = norma?.resumenAsistenteFecha || null;
    setGenerandoAnalisis(true);
    accessAPI(
      "POST",
      `admin/norma/${normaId}/analisis-requerimientos/generar`,
      {},
      () => {
        const inicio = Date.now();
        const intervalo = setInterval(() => {
          accessAPI(
            "GET",
            `admin/norma/${normaId}`,
            null,
            (r) => {
              if (r?.resumenAsistenteFecha && r.resumenAsistenteFecha !== fechaPrevia) {
                clearInterval(intervalo);
                setGenerandoAnalisis(false);
                setNorma((prev) => ({
                  ...prev,
                  resumenAsistente: r.resumenAsistente,
                  resumenAsistenteFecha: r.resumenAsistenteFecha
                }));
                toast.current?.show({
                  severity: "success",
                  summary: "Listo",
                  detail: "Análisis de requerimientos generado",
                  life: 3000
                });
              } else if (Date.now() - inicio > 180000) {
                clearInterval(intervalo);
                setGenerandoAnalisis(false);
              }
            },
            () => {}
          );
        }, 6000);
      },
      (r) => {
        setGenerandoAnalisis(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: r?.[0]?.msg || "No se pudo generar el análisis",
          sticky: true
        });
      }
    );
  }

  function cargarNorma() {
    accessAPI(
      "GET",
      `admin/norma/${normaId}`,
      null,
      (respuesta) => {
        setNorma(respuesta);
        document.title = respuesta.numero;
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar la norma",
          sticky: true
        });
      }
    );
  }

  function editarNormaSubmit(respuestasFormulario) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/norma/${normaId}`,
      respuestasFormulario,
      (respuesta) => {
        setNorma((prev) => ({ ...prev, ...respuesta }));
        document.title = respuesta.numero;
        setSidebarEditar(false);
        setLoaderEditar(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Norma actualizada exitosamente"
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar la norma",
          sticky: true
        });
        setLoaderEditar(false);
      }
    );
  }

  function agregarPunto(datosFormulario) {
    setLoaderNuevoPunto(true);
    accessAPI(
      "POST",
      `admin/norma/${normaId}/puntos`,
      { texto: datosFormulario.texto },
      (respuesta) => {
        setNorma((prev) => ({ ...prev, puntos: respuesta }));
        setSidebarNuevoPunto(false);
        setLoaderNuevoPunto(false);
        toast.current?.show({
          severity: "success",
          summary: "Agregado",
          detail: "Punto agregado exitosamente"
        });
      },
      (respuesta) => {
        setLoaderNuevoPunto(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al agregar el punto",
          sticky: true
        });
      }
    );
  }

  function eliminarPunto(rowData) {
    accessAPI(
      "DELETE",
      `admin/norma/${normaId}/puntos/${rowData.index}`,
      null,
      (respuesta) => {
        setNorma((prev) => ({ ...prev, puntos: respuesta }));
        toast.current?.show({
          severity: "success",
          summary: "Eliminado",
          detail: "Punto eliminado"
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar el punto",
          sticky: true
        });
      }
    );
  }

  function onRowReorder(e) {
    const nuevosPuntos = e.value.map(({ index, ...rest }) => rest);
    setNorma((prev) => ({ ...prev, puntos: nuevosPuntos }));
    accessAPI(
      "PUT",
      `admin/norma/${normaId}/puntos/reordenar`,
      { puntos: nuevosPuntos },
      () => {},
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al reordenar los puntos",
          sticky: true
        });
      }
    );
  }

  function toggleSeguimiento(index, seguimiento) {
    setPuntoActualizandoIdx(index);
    accessAPI(
      "PUT",
      `admin/norma/${normaId}/puntos/${index}/seguimiento`,
      { seguimiento },
      (respuesta) => {
        setNorma((prev) => ({ ...prev, puntos: respuesta }));
        setPuntoActualizandoIdx(null);
      },
      (respuesta) => {
        setPuntoActualizandoIdx(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar el punto",
          sticky: true
        });
      }
    );
  }


  function eliminarArchivo() {
    confirmDialog({
      message: `¿Eliminar el archivo "${norma.archivo?.nombredescarga}"?`,
      header: "Confirmar eliminación",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      accept: () => {
        accessAPI(
          "DELETE",
          `admin/norma/${normaId}/archivo`,
          null,
          () => {
            setNorma((prev) => ({ ...prev, archivo: null }));
            toast.current?.show({
              severity: "success",
              summary: "Eliminado",
              detail: "Archivo eliminado"
            });
          },
          (respuesta) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al eliminar el archivo",
              sticky: true
            });
          }
        );
      }
    });
  }

  const editarFormConfig = norma
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "numero",
                tipoitemformularioid: 1,
                etiqueta: "Número",
                requerido: true,
                respuesta: norma.numero
              },
              {
                nombre: "nombre",
                tipoitemformularioid: 1,
                etiqueta: "Nombre",
                requerido: true,
                respuesta: norma.nombre
              },
              {
                nombre: "nombrecertificado",
                tipoitemformularioid: 1,
                etiqueta: "Nombre en el certificado",
                requerido: true,
                respuesta: norma.nombrecertificado
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar cambios",
            callback: editarNormaSubmit,
            validar: true
          }
        ]
      }
    : null;

  const puntosData =
    Array.isArray(norma?.puntos)
      ? norma.puntos.map((p, idx) => ({ ...p, index: idx }))
      : [];

  const menuItems = [
    {
      label: "Eliminar",
      icon: "pi pi-trash",
      className: "text-red-500",
      command: () => {
        const punto = filaSeleccionada.current;
        confirmDialog({
          message: "¿Eliminar este punto?",
          header: "Confirmar eliminación",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Eliminar",
          rejectLabel: "Cancelar",
          acceptClassName: "p-button-danger",
          accept: () => eliminarPunto(punto)
        });
      }
    }
  ];

  const nuevoPuntoFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "texto",
            tipoitemformularioid: 1,
            etiqueta: "Punto",
            requerido: true
          }
        ]
      }
    ],
    botones: [
      { etiqueta: "Agregar punto", callback: agregarPunto, validar: true }
    ]
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={menuItems} popup ref={menuRef} />

      {!norma && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "1em"
          }}
        >
          {/* Título skeleton */}
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div css={{ display: "flex", alignItems: "center", gap: "1em" }}>
              <Skeleton width="2rem" height="2rem" shape="circle" />
              <Skeleton width="15rem" height="2rem" />
            </div>
          </div>

          {/* Card detalles skeleton */}
          <Card>
            <Skeleton
              width="6rem"
              height="1.5rem"
              css={{ marginBottom: "1.5em" }}
            />
            <div className="grid">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="col-12 md:col-3">
                  <Skeleton
                    width="50%"
                    height="0.8rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="80%" height="1.2rem" />
                </div>
              ))}
            </div>
          </Card>

          {/* Card puntos skeleton */}
          <Card>
            <Skeleton
              width="12rem"
              height="1.5rem"
              css={{ marginBottom: "1.5em" }}
            />
            <Skeleton width="100%" height="3rem" />
          </Card>
        </div>
      )}

      {norma && (
        <>
          <Titulo urlVolver="/norma">{norma.numero}</Titulo>

          <div className="grid" css={{ marginTop: "1em" }}>
            <div className="col-12 lg:col-6">
              <Card>
                <Titulo
                  botones={
                    <Boton icono negro callback={() => setSidebarEditar(true)}>
                      <i className="pi pi-pencil" />
                    </Boton>
                  }
                >
                  Detalles
                </Titulo>
                <div className="grid">
                  <CampoInformacion nombreCampo="Número">
                    {norma.numero}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Nombre">
                    {norma.nombre}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Nombre en el certificado">
                    {norma.nombrecertificado}
                  </CampoInformacion>
                  {/* Vigentes; las altas y bajas se manejan desde cada
                      acreditación (sección Acreditación) */}
                  <CampoInformacion nombreCampo="Acreditaciones">
                    {(norma.norma_x_acreditacion || []).length > 0 ? (
                      <span
                        css={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.3em"
                        }}
                      >
                        {norma.norma_x_acreditacion.map((vinculo) => (
                          <span
                            key={vinculo.id}
                            css={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5em"
                            }}
                          >
                            {vinculo.acreditacion?.organismoAcreditacion}
                            {vinculo.suspendida && (
                              <Tag
                                value="Suspendida"
                                severity="danger"
                                css={{ fontSize: "0.7em" }}
                              />
                            )}
                          </span>
                        ))}
                      </span>
                    ) : (
                      "Sin acreditación"
                    )}
                  </CampoInformacion>
                </div>
              </Card>

              <Card css={{ marginTop: "1em" }}>
                <Titulo
                  botones={
                    <Boton texto callback={() => setSidebarAnalisis(true)}>
                      Ver análisis
                    </Boton>
                  }
                >
                  Análisis de requerimientos (IA)
                </Titulo>
                {norma.resumenAsistente ? (
                  <p css={{ color: "var(--grisTextos)" }}>
                    Generado
                    {norma.resumenAsistenteFecha
                      ? ` el ${formatFecha(norma.resumenAsistenteFecha)}`
                      : ""}
                    . Se envía al asistente en lugar del PDF de la norma.
                  </p>
                ) : (
                  <p css={{ color: "var(--grisTextos)" }}>
                    {norma.archivo
                      ? "Sin generar. Se genera automáticamente al subir el PDF, o con el botón."
                      : "Cargá un PDF de la norma para poder generar el análisis."}
                  </p>
                )}
              </Card>

              <CardEquivalencias
                normaId={norma.id}
                equivalencias={norma.normaEquivalencias}
                onChange={(nuevas) =>
                  setNorma((prev) => ({ ...prev, normaEquivalencias: nuevas }))
                }
                toastRef={toast}
              />

              <Card css={{ marginTop: "1em" }}>
                <Titulo
                  botones={
                    norma.archivo ? (
                      <Boton icono rojo callback={eliminarArchivo}>
                        <i className="pi pi-trash" />
                      </Boton>
                    ) : (
                      <SubidorArchivos
                        presignEndpoint="admin/archivo/presign"
                        confirmarEndpoint={`admin/norma/${normaId}/archivo/confirmar`}
                        ubicacion="archivosnormas"
                        icono="pi pi-upload"
                        onSubido={(respuesta) =>
                          setNorma((prev) => ({
                            ...prev,
                            archivo: {
                              id: respuesta.id,
                              nombredescarga: respuesta.nombredescarga,
                              hash: respuesta.hash
                            },
                            estadocapitulos: respuesta.estadocapitulos,
                            puntos: []
                          }))
                        }
                      />
                    )
                  }
                >
                  Archivo
                </Titulo>
                {norma.archivo ? (
                  <ArchivoPreview
                    nombre={norma.archivo.nombredescarga}
                    hash={norma.archivo.hash}
                    sinDescarga
                    sinToolbar
                  />
                ) : (
                  <p css={{ color: "var(--grisTextos)" }}>
                    No hay archivo cargado
                  </p>
                )}
              </Card>
            </div>

            <div className="col-12 lg:col-6">
              <Card>
                <Titulo
                  botones={
                    <Boton
                      icono
                      negro
                      callback={() => setSidebarNuevoPunto(true)}
                    >
                      <i className="pi pi-plus" />
                    </Boton>
                  }
                >
                  Puntos
                </Titulo>
                {norma.estadocapitulos?.status === "procesando" ? (
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5em",
                      color: "var(--grisTextos)",
                      padding: "0.5em 0"
                    }}
                  >
                    <i className="pi pi-spin pi-spinner" />
                    Analizando capítulos con IA, puede demorar unos minutos…
                  </div>
                ) : (
                <TablaFiltrable conFiltros={false}
                  value={puntosData}
                  nombreArchivo="puntos_norma"
                  size="small"
                  emptyMessage="No hay puntos cargados"
                  reorderableRows
                  onRowReorder={onRowReorder}
                  dataKey="index"
                >
                  <Column rowReorder style={{ width: "3rem" }} />
                  <Column field="texto" header="Punto" />
                  <Column
                    header="Seguimiento"
                    style={{ width: "8rem", textAlign: "center" }}
                    body={(rowData) => (
                      <InputSwitch
                        checked={!!rowData.seguimiento}
                        disabled={puntoActualizandoIdx === rowData.index}
                        onChange={(e) =>
                          toggleSeguimiento(rowData.index, e.value)
                        }
                      />
                    )}
                  />
                  <Column
                    body={(rowData) => (
                      <Boton
                        icono
                        negro
                        chico
                        callback={(e) => {
                          filaSeleccionada.current = rowData;
                          menuRef.current.toggle(e);
                        }}
                      >
                        <i className="pi pi-ellipsis-v" />
                      </Boton>
                    )}
                    style={{ width: "3rem", textAlign: "center" }}
                  />
                </TablaFiltrable>
                )}
              </Card>
            </div>
          </div>

          <Sidebar
            visible={sidebarEditar}
            position="right"
            onHide={() => setSidebarEditar(false)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Editar norma</Titulo>
            {editarFormConfig && (
              <Formulario
                key={norma.id + "-" + norma.numero}
                config={editarFormConfig}
                loader={loaderEditar}
              />
            )}
          </Sidebar>

          <Sidebar
            visible={sidebarNuevoPunto}
            position="right"
            onHide={() => setSidebarNuevoPunto(false)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Nuevo punto</Titulo>
            <Formulario
              config={nuevoPuntoFormConfig}
              loader={loaderNuevoPunto}
              key={puntosData.length}
            />
          </Sidebar>

          {/* Sidebar del análisis de requerimientos (vista Markdown / edición) */}
          <Sidebar
            visible={sidebarAnalisis}
            position="right"
            onHide={() => {
              setSidebarAnalisis(false);
              setEditandoAnalisis(false);
            }}
            onShow={renderizarFormulas}
            className="w-full lg:w-8"
          >
            {!editandoAnalisis && (
              <div
                css={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "0.5em",
                  marginBottom: "0.5em"
                }}
              >
                {norma.archivo && (
                  <Boton
                    azul
                    transparente
                    loader={generandoAnalisis}
                    callback={generarAnalisis}
                  >
                    <i className="pi pi-sparkles" />{" "}
                    {norma.resumenAsistente
                      ? "Repetir análisis"
                      : "Generar análisis"}
                  </Boton>
                )}
                {norma.resumenAsistente && (
                  <Boton
                    azul
                    transparente
                    callback={() => setEditandoAnalisis(true)}
                  >
                    <i className="pi pi-pencil" /> Editar análisis
                  </Boton>
                )}
              </div>
            )}

            {editandoAnalisis ? (
              <Formulario
                key={norma.resumenAsistenteFecha}
                config={editarAnalisisFormConfig}
                loader={loaderEditarAnalisis}
              />
            ) : !norma.resumenAsistente ? (
              <p css={{ color: "var(--grisTextos)" }}>
                {norma.archivo
                  ? "Sin generar. Se genera automáticamente al subir el PDF, o con el botón."
                  : "Cargá un PDF de la norma para poder generar el análisis."}
              </p>
            ) : (
              <div
                ref={analisisRef}
                css={{
                  "& h1, & h2, & h3, & h4": {
                    marginTop: "1em",
                    marginBottom: "0.4em",
                    lineHeight: 1.3
                  },
                  "& p, & li": { lineHeight: 1.5 },
                  "& ul, & ol": { paddingLeft: "1.5em" },
                  "& table": {
                    borderCollapse: "collapse",
                    width: "100%",
                    margin: "0.5em 0",
                    fontSize: "0.85rem"
                  },
                  "& th, & td": {
                    border: "1px solid var(--grisBordes, #e0e0e0)",
                    padding: "4px 8px",
                    textAlign: "left"
                  },
                  "& th": { backgroundColor: "var(--grisFondo, #f5f5f5)" },
                  "& code": {
                    backgroundColor: "var(--grisFondo, #f5f5f5)",
                    padding: "1px 4px",
                    borderRadius: "3px"
                  },
                  "& img": { display: "none" },
                  overflowX: "auto"
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    marked.parse(norma.resumenAsistente || "")
                  )
                }}
              />
            )}
          </Sidebar>
        </>
      )}
    </>
  );
}
